import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { User as UserIcon, Mail, Calendar, Edit2, Camera, X, Save, Loader2 } from 'lucide-react';
import { profileAPI } from '../services/api';
import { formatDate } from '../utils/constants';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ full_name: '', nickname: '' });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      setUser(response.data);
      setEditData({
        full_name: response.data.full_name || '',
        nickname: response.data.nickname || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await profileAPI.updateProfile(editData);
      setUser(response.data.user);
      setEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64Data = event.target.result;
          await profileAPI.uploadPhoto({ photo_data: base64Data });
          await loadProfile();
          setPhotoDialogOpen(false);
          toast.success('Profile photo updated!');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload photo');
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await profileAPI.deletePhoto();
      await loadProfile();
      setPhotoDialogOpen(false);
      toast.success('Profile photo removed');
    } catch (error) {
      toast.error('Failed to delete photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-white text-xl font-secondary">Loading...</div>
        </div>
      </Layout>
    );
  }

  const displayName = user?.nickname || user?.full_name || 'CryptoPay User';

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-primary font-bold text-white mb-2">PROFILE</h1>
              <p className="text-gray-400 font-secondary">Your account information</p>
            </div>
            {!editing && (
              <Button
                onClick={() => setEditing(true)}
                className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4"
                data-testid="edit-profile-btn"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            )}
          </div>

          {/* Profile Card */}
          <div className="glass-card p-8 rounded-2xl">
            <div className="flex items-center gap-6 mb-8">
              {/* Profile Photo */}
              <div className="relative group">
                <div 
                  className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center cursor-pointer"
                  onClick={() => setPhotoDialogOpen(true)}
                  data-testid="profile-photo-container"
                >
                  {user?.profile_photo ? (
                    <img 
                      src={user.profile_photo} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-12 h-12 text-white" />
                  )}
                </div>
                <button
                  onClick={() => setPhotoDialogOpen(true)}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-[#7000FF] rounded-full flex items-center justify-center border-2 border-[#0A051E] hover:bg-[#5B00CC] transition-colors"
                  data-testid="change-photo-btn"
                >
                  <Camera className="w-4 h-4 text-white" />
                </button>
              </div>
              
              <div className="flex-1">
                {editing ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-400 text-xs mb-1 block">Full Name</Label>
                      <Input
                        value={editData.full_name}
                        onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                        className="bg-white/5 border-white/10 text-white text-xl font-primary font-bold"
                        placeholder="Your full name"
                        data-testid="edit-fullname-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs mb-1 block">Nickname (Display Name)</Label>
                      <Input
                        value={editData.nickname}
                        onChange={(e) => setEditData({ ...editData, nickname: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="How would you like to be called?"
                        maxLength={30}
                        data-testid="edit-nickname-input"
                      />
                      <p className="text-gray-500 text-xs mt-1">{editData.nickname?.length || 0}/30 characters</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-primary font-bold text-white mb-1" data-testid="profile-display-name">
                      {displayName}
                    </h2>
                    {user?.nickname && user?.full_name && (
                      <p className="text-gray-400 font-secondary text-sm">{user.full_name}</p>
                    )}
                    <p className="text-[#00F0FF] font-secondary">CryptoPay Member</p>
                  </>
                )}
              </div>
            </div>

            {/* Edit Actions */}
            {editing && (
              <div className="flex gap-3 mb-6">
                <Button
                  onClick={() => {
                    setEditing(false);
                    setEditData({ full_name: user?.full_name || '', nickname: user?.nickname || '' });
                  }}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-3"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex-1 bg-[#00FF94] hover:bg-[#00CC77] text-black rounded-full py-3 font-bold"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
            )}

            {/* Info Fields */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl" data-testid="profile-email">
                <div className="w-10 h-10 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Email</div>
                  <div className="text-white font-secondary">{user?.email}</div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl" data-testid="profile-member-since">
                <div className="w-10 h-10 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#7000FF]" />
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Member Since</div>
                  <div className="text-white font-secondary">{formatDate(user?.created_at)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Stats */}
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-xl font-primary font-bold text-white mb-6">ACCOUNT STATUS</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-gray-400 text-sm mb-2">Account Type</div>
                <div className="text-white font-secondary font-semibold">Standard</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-gray-400 text-sm mb-2">Verification</div>
                <div className="text-[#00FF94] font-secondary font-semibold">Verified</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Photo Upload Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="bg-[#0A051E] border-white/10 text-white max-w-md" aria-describedby="photo-dialog-description">
          <DialogHeader>
            <DialogTitle className="font-primary text-2xl">PROFILE PHOTO</DialogTitle>
          </DialogHeader>
          <p id="photo-dialog-description" className="sr-only">Upload or remove your profile photo</p>
          
          <div className="space-y-6 mt-4">
            {/* Current Photo Preview */}
            <div className="flex justify-center">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center">
                {user?.profile_photo ? (
                  <img 
                    src={user.profile_photo} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserIcon className="w-16 h-16 text-white" />
                )}
              </div>
            </div>

            {/* Upload Options */}
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-4"
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {uploadingPhoto ? 'Uploading...' : 'Upload New Photo'}
              </Button>

              {user?.profile_photo && (
                <Button
                  onClick={handleDeletePhoto}
                  disabled={uploadingPhoto}
                  className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-full py-4"
                  data-testid="remove-photo-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Remove Photo
                </Button>
              )}
            </div>

            <p className="text-gray-400 text-sm text-center">
              Supported formats: JPG, PNG, GIF • Max size: 2MB
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Profile;
